import { forwardRef, useImperativeHandle } from 'react';
import { FormProvider } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';

import { PAYRUN_TYPE_ID, PayrunApproverEdit, payrunTypeItems } from '@xemplo/gp-types';
import { useGetPayschedulesQuery } from '@xemplo/pay-schedule-query';
import { useGetPayrunApproversDetailQuery } from '@xemplo/payrun-query';
import { useListBuilder } from '@xemplo/stateful-list-core';
import { V2 } from '@xemplo/text-dropdown';
import { V3 } from '@xemplo/text-dropdown-multi';

import * as S from './edit-payrun-approvers.styles';
import { FormFields } from './edit-payrun-approvers.types';
import { EditPayrunApprovalRef } from './edit-payrun-approvers.types';
import { useApproversFormHandler } from './use-approvers-form-handler';

export const EditPayrunApprovers = forwardRef<EditPayrunApprovalRef, PayrunApproverEdit>(
  (props, ref) => {
    //Load Data
    const { data: approversData } = useGetPayrunApproversDetailQuery(props);
    const { data: paySchedulesData } = useGetPayschedulesQuery({
      filter: { companyIds: [props.companyId], isActive: 1 },
    });

    //Initialise form with default values
    const methods = useForm<FormFields>({
      defaultValues: {
        payrunType: approversData?.payrunTypeId,
        paySchedules: approversData?.payschedules?.map((p) => p.id),
      },
      shouldUnregister: true,
    });

    //Build Stateful List
    const payrunTypeList = useListBuilder(payrunTypeItems);
    const payScheduleList = useListBuilder(paySchedulesData?.payScheduleItems ?? []);

    //Other form Stuff
    const { payrunType } = methods.watch();
    const disablePayScheduleField = payrunType !== PAYRUN_TYPE_ID.SCHEDULED;
    const formHandler = useApproversFormHandler({ ...props, methods });
    useImperativeHandle(ref, () => formHandler, [formHandler]);

    return (
      <FormProvider {...methods}>
        <DevTool control={methods.control} />
        <S.StyledForm>
          <S.FormLabel>
            Pay run type
            <V2.TextDropdownRhf
              name="payrunType"
              items={payrunTypeList}
              aria-label="payItems"
              onChange={() => methods.setValue('paySchedules', undefined)}
              allowDeselect={false}
              placeholder="select a payrun type"
            />
          </S.FormLabel>

          <S.FormLabel>
            Specific Pay Schedule(s)
            <V3.TextDropdownMultiRhf
              items={payScheduleList}
              aria-label="pay schedule list"
              name="paySchedules"
              disabled={disablePayScheduleField}
            />
          </S.FormLabel>
        </S.StyledForm>
      </FormProvider>
    );
  },
);
